import Radio, { RadioInstance } from "../../../Radio";
import { EAudioVisualizerSource } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

export type AudioDataCallback = (data: Uint8Array, bufferLength: number) => void;

interface SourceConfig {
  fftSize: number;
  smoothing: number;
}

export class AudioVisualizerService {
  private static instance: AudioVisualizerService;

  private analysers: Map<string, AnalyserNode> = new Map();
  private dataArrays: Map<string, Uint8Array> = new Map();
  private listeners: Map<string, Set<AudioDataCallback>> = new Map();
  private sourceConfigs: Map<string, SourceConfig> = new Map();
  private timerId: ReturnType<typeof setTimeout> | null = null;

  private constructor() {}

  public static getInstance(): AudioVisualizerService {
    if (!AudioVisualizerService.instance) {
      AudioVisualizerService.instance = new AudioVisualizerService();
    }
    return AudioVisualizerService.instance;
  }

  private getAnalyserRef(source: string): AnalyserNode | null {
    const radio = RadioInstance ?? new Radio();

    // Handle video area sources with format "video_area::<videoId>"
    if (source.startsWith(EAudioVisualizerSource.VIDEO_AREA + "::")) {
      const videoId = source.slice((EAudioVisualizerSource.VIDEO_AREA + "::").length);
      const analyser = radio.GlobalAudio.getVideoAnalyser(videoId);
      return analyser;
    }

    switch (source) {
      case EAudioVisualizerSource.MIXED:
        return radio.GlobalAudio.getVisualizerAnalyser();
      case EAudioVisualizerSource.MICROPHONE:
        return radio.Microphones.getAnalyser();
      case EAudioVisualizerSource.PLAYLIST:
        return radio.Playlist.getAnalyser();
      case EAudioVisualizerSource.SYSTEM_AUDIO:
        return radio.SystemAudio.getAnalyser();
      default:
        return null;
    }
  }

  public getConnectedVideoIds(): string[] {
    const radio = RadioInstance ?? new Radio();
    return radio.GlobalAudio.getConnectedVideoIds();
  }

  private configureAnalyser(analyser: AnalyserNode, fftSize: number, smoothing: number): void {
    if (analyser.fftSize !== fftSize) {
      analyser.fftSize = fftSize;
    }
    if (analyser.smoothingTimeConstant !== smoothing) {
      analyser.smoothingTimeConstant = smoothing;
    }
  }

  public connect(
    source: EAudioVisualizerSource | string,
    fftSize: number = 512,
    smoothing: number = 0.8
  ): void {
    const key = source;

    // Store the config so we can lazily resolve the analyser later
    this.sourceConfigs.set(key, { fftSize, smoothing });
    this._sourcesDirty = true;

    if (!this.analysers.has(key)) {
      const analyser = this.getAnalyserRef(source);
      if (analyser) {
        this.configureAnalyser(analyser, fftSize, smoothing);
        this.analysers.set(key, analyser);
        this.dataArrays.set(key, new Uint8Array(analyser.frequencyBinCount));
      }
    }

    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    this.startTicker();
  }

  public subscribe(source: EAudioVisualizerSource | string, callback: AudioDataCallback): void {
    if (!this.listeners.has(source)) {
      this.listeners.set(source, new Set());
    }
    this.listeners.get(source)!.add(callback);
    this.startTicker();
  }

  public unsubscribe(source: EAudioVisualizerSource | string, callback: AudioDataCallback): void {
    const sourceListeners = this.listeners.get(source);
    if (sourceListeners) {
      sourceListeners.delete(callback);
      if (sourceListeners.size === 0) {
        this.listeners.delete(source);
        this.analysers.delete(source);
        this.dataArrays.delete(source);
        this.sourceConfigs.delete(source);
      }
    }

    // Stop ticker if no listeners remain
    if (this.getTotalListeners() === 0) {
      this.stopTicker();
    }
  }

  private getTotalListeners(): number {
    let total = 0;
    for (const set of this.listeners.values()) {
      total += set.size;
    }
    return total;
  }

  private static readonly TICK_INTERVAL = 50; // 20fps — sufficient for audio visualization

  private startTicker(): void {
    if (this.timerId !== null) return;
    this.timerId = setTimeout(this.tick, AudioVisualizerService.TICK_INTERVAL);
  }

  private stopTicker(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private _sourcesDirty = false;

  private tick = (): void => {
    this.timerId = null;
    if (this.getTotalListeners() === 0) return;
    this.timerId = setTimeout(this.tick, AudioVisualizerService.TICK_INTERVAL);

    const shouldReResolve = this._sourcesDirty;
    if (shouldReResolve) this._sourcesDirty = false;

    for (const [source, config] of this.sourceConfigs) {
      const sourceListeners = this.listeners.get(source);
      if (!sourceListeners || sourceListeners.size === 0) continue;

      let analyser = this.analysers.get(source);

      // Lazily resolve analysers that aren't connected yet
      if (!analyser) {
        analyser = this.getAnalyserRef(source) ?? undefined;
        if (analyser) {
          this.configureAnalyser(analyser, config.fftSize, config.smoothing);
          this.analysers.set(source, analyser);
          this.dataArrays.set(source, new Uint8Array(analyser.frequencyBinCount));
        } else {
          continue;
        }
      }

      // Periodically check if the source analyser changed (e.g. new track, mic switched)
      if (shouldReResolve) {
        const freshAnalyser = this.getAnalyserRef(source);
        if (freshAnalyser && freshAnalyser !== analyser) {
          analyser = freshAnalyser;
          this.configureAnalyser(analyser, config.fftSize, config.smoothing);
          this.analysers.set(source, analyser);
          this.dataArrays.set(source, new Uint8Array(analyser.frequencyBinCount));
        }
      }

      const dataArray = this.dataArrays.get(source);
      if (!dataArray) continue;

      analyser.getByteFrequencyData(dataArray);

      for (const listener of sourceListeners) {
        listener(dataArray, analyser.frequencyBinCount);
      }
    }
  };

  /** Signal that audio sources may have changed (e.g. new track, mic switch) */
  public markDirty(): void {
    this._sourcesDirty = true;
  }

  public disconnect(source: EAudioVisualizerSource | string): void {
    this.listeners.delete(source);
    this.analysers.delete(source);
    this.dataArrays.delete(source);
    this.sourceConfigs.delete(source);
    this._sourcesDirty = true;

    if (this.getTotalListeners() === 0) {
      this.stopTicker();
    }
  }

  public destroy(): void {
    this.stopTicker();
    this.listeners.clear();
    this.analysers.clear();
    this.dataArrays.clear();
    this.sourceConfigs.clear();
  }
}
